import { useEffect, useMemo, useState } from "react";
import { showToast } from "../../libraries/clipboardUtils";
import {
  fetchAttendanceRecords,
  fetchHolidayRecords,
  findEmployeeRecord,
  formatClockTime,
  formatDuration,
  getEmployeeName,
  getHolidayForDate,
  getLocalDateKey,
  parseDateKey
} from "../../libraries/tecgymAdminAttendance";
import { fetchTecgymAdminUsers } from "../../libraries/tecgymAdminUsers";

const REPORT_COLUMNS = [
  { key: "index", label: "#", width: 52, align: "center" },
  { key: "name", label: "Employee", width: 260, align: "left" },
  { key: "checkIn", label: "Check In", width: 180, align: "left" },
  { key: "checkInImage", label: "Check In Image", width: 130, align: "center", image: true },
  { key: "checkOut", label: "Check Out", width: 250, align: "left" },
  { key: "checkOutImage", label: "Check Out Image", width: 140, align: "center", image: true },
  { key: "working", label: "Working Hrs", width: 130, align: "left" },
  { key: "deficit", label: "Deficit Hrs", width: 130, align: "left" },
  { key: "overtime", label: "Overtime", width: 130, align: "left" },
  { key: "status", label: "Status", width: 150, align: "left" }
];

function getSheetColumns(includeImages) {
  return includeImages ? REPORT_COLUMNS : REPORT_COLUMNS.filter((column) => !column.image);
}

function getSheetTableWidth(columns) {
  return columns.reduce((total, column) => total + column.width, 0);
}

function formatDailyDateLabel(dateKey) {
  const date = parseDateKey(dateKey);
  if (Number.isNaN(date.getTime())) {
    return dateKey;
  }

  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
}

function getDayClosure(dateKey, holidays) {
  const date = parseDateKey(dateKey);
  const holiday = getHolidayForDate(holidays, dateKey);
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;

  if (holiday) {
    return { isClosed: true, note: `Holiday — ${holiday.occasion}` };
  }

  if (isWeekend) {
    return { isClosed: true, note: "Weekend — office closed" };
  }

  return { isClosed: false, note: "" };
}

function getRowStatus(record, closure) {
  if (record?.checkInTime) {
    if (record.isLate) {
      return { label: "Late", color: "#dc2626" };
    }

    if (!record.checkOutTime) {
      return { label: "Checked in", color: "#d97706" };
    }

    return { label: "Present", color: "#15803d" };
  }

  if (closure.note.startsWith("Holiday")) {
    return { label: "Holiday", color: "#1d4ed8" };
  }

  if (closure.isClosed) {
    return { label: "Weekend", color: "#be123c" };
  }

  return { label: "Absent", color: "#6b7280" };
}

function rowFromAttendance(name, record, closure) {
  return {
    name: name || record?.employeeName || "Employee",
    checkIn: formatClockTime(record?.checkInTime),
    checkInImage: record?.checkInImage || "",
    checkOut: formatClockTime(record?.checkOutTime),
    checkOutImage: record?.checkOutImage || "",
    working: formatDuration(record?.workingMinutes),
    deficit: formatDuration(record?.deficitMinutes),
    overtime: (Number(record?.overtimeMinutes) || 0) > 0 ? formatDuration(record.overtimeMinutes) : "—",
    overtimeMinutes: Number(record?.overtimeMinutes) || 0,
    isLate: Boolean(record?.isLate),
    isEarlyCheckout: Boolean(record?.isEarlyCheckout),
    status: getRowStatus(record, closure)
  };
}

function buildDailyRows(employees, records, dateKey, closure) {
  const dayRecords = records.filter((record) => record.dateKey === dateKey);
  const usedRecords = new Set();
  const rows = [...employees]
    .sort((employeeA, employeeB) => getEmployeeName(employeeA).localeCompare(
      getEmployeeName(employeeB),
      undefined,
      { sensitivity: "base" }
    ))
    .map((employee) => {
      const record = dayRecords.find((item) => (
        !usedRecords.has(item) && findEmployeeRecord([item], employee, dateKey)
      ));

      if (record) {
        usedRecords.add(record);
      }

      return rowFromAttendance(getEmployeeName(employee), record, closure);
    });

  dayRecords.forEach((record) => {
    if (usedRecords.has(record)) {
      return;
    }

    rows.push(rowFromAttendance(record.employeeName, record, closure));
  });

  return rows
    .sort((rowA, rowB) => rowA.name.localeCompare(rowB.name, undefined, { sensitivity: "base" }))
    .map((row, index) => ({ ...row, index: index + 1 }));
}

function summarizeRows(rows, closure) {
  return rows.reduce((summary, row) => {
    summary.total += 1;

    if (row.status.label === "Present") {
      summary.present += 1;
    } else if (row.status.label === "Checked in") {
      summary.checkedIn += 1;
    } else if (row.status.label === "Late") {
      summary.late += 1;
    } else if (!closure.isClosed) {
      summary.absent += 1;
    }

    summary.overtimeMinutes += Number(row.overtimeMinutes) || 0;
    return summary;
  }, { total: 0, present: 0, checkedIn: 0, late: 0, absent: 0, overtimeMinutes: 0 });
}

function getSheetLayout(closure, rowCount) {
  const headerHeight = closure.note ? 184 : 156;
  const headHeight = 46;
  const rowHeight = 68;
  const footerHeight = 42;

  return {
    tableTop: headerHeight,
    headHeight,
    rowHeight,
    height: headerHeight + headHeight + (rowCount * rowHeight) + footerHeight
  };
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }

    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);

    if (!String(src).startsWith("data:")) {
      image.crossOrigin = "anonymous";
    }

    image.src = src;
  });
}

function traceRoundRect(context, x, y, width, height, radius) {
  const corner = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + corner, y);
  context.arcTo(x + width, y, x + width, y + height, corner);
  context.arcTo(x + width, y + height, x, y + height, corner);
  context.arcTo(x, y + height, x, y, corner);
  context.arcTo(x, y, x + width, y, corner);
  context.closePath();
}

function fitText(context, text, maxWidth) {
  const value = String(text || "");
  if (context.measureText(value).width <= maxWidth) {
    return value;
  }

  let trimmed = value;
  while (trimmed.length > 1 && context.measureText(`${trimmed}…`).width > maxWidth) {
    trimmed = trimmed.slice(0, -1);
  }

  return `${trimmed}…`;
}

function sheetPalette() {
  const light = document.documentElement.getAttribute("data-theme") !== "dark";
  if (!light) {
    return {
      page: "#121418",
      card: "#1a1d23",
      brand: "#4ade80",
      title: "#ffffff",
      date: "#d1d5db",
      summary: "#9ca3af",
      holiday: "#93c5fd",
      weekend: "#fca5a5",
      header: "#102235",
      headerText: "#ffffff",
      headerLine: "#187718",
      row: "#1a1d23",
      rowAlt: "#20232b",
      line: "#333",
      text: "#ffffff",
      muted: "#9ca3af",
      mark: "#f87171",
      overtime: "#4ade80",
      photoBorder: "#444",
      footer: "#6b7280"
    };
  }

  return {
    page: "#f8f9fa",
    card: "#ffffff",
    brand: "#15803d",
    title: "#212529",
    date: "#495057",
    summary: "#6b7280",
    holiday: "#1d4ed8",
    weekend: "#be123c",
    header: "#f1f3f5",
    headerText: "#495057",
    headerLine: "#187718",
    row: "#ffffff",
    rowAlt: "#f8f9fa",
    line: "#e9ecef",
    text: "#212529",
    muted: "#6b7280",
    mark: "#dc2626",
    overtime: "#15803d",
    photoBorder: "#ced4da",
    footer: "#6b7280"
  };
}

function drawCoverImage(context, image, x, y, size, borderColor) {
  context.save();
  traceRoundRect(context, x, y, size, size, 8);
  context.clip();
  const scale = Math.max(size / image.width, size / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  context.drawImage(image, x + (size - width) / 2, y + (size - height) / 2, width, height);
  context.restore();
  context.save();
  traceRoundRect(context, x, y, size, size, 8);
  context.strokeStyle = borderColor;
  context.lineWidth = 1;
  context.stroke();
  context.restore();
}

function drawDailySheet(context, { dateLabel, closure, rows, summary, images, columns, tableWidth, width }) {
  const padding = 36;
  const colors = sheetPalette();
  const { tableTop, headHeight, rowHeight, height } = getSheetLayout(closure, rows.length);

  context.fillStyle = colors.page;
  context.fillRect(0, 0, width, height);

  context.fillStyle = colors.card;
  traceRoundRect(context, 20, 16, width - 40, height - 32, 16);
  context.fill();

  context.fillStyle = colors.brand;
  context.font = "700 13px Segoe UI, Arial, sans-serif";
  context.textAlign = "left";
  context.textBaseline = "top";
  context.fillText("TECGYM ADMIN", padding, 36);

  context.fillStyle = colors.title;
  context.font = "800 28px Segoe UI, Arial, sans-serif";
  context.fillText("Daily Attendance", padding, 56);

  context.fillStyle = colors.date;
  context.font = "600 16px Segoe UI, Arial, sans-serif";
  context.fillText(dateLabel, padding, 94);

  const summaryText = `Total ${summary.total}    Present ${summary.present}    Checked in ${summary.checkedIn}    Late ${summary.late}    Absent ${summary.absent}    `;
  context.fillStyle = colors.summary;
  context.font = "700 14px Segoe UI, Arial, sans-serif";
  context.fillText(summaryText, padding, 118);
  context.fillStyle = colors.overtime;
  context.fillText(
    `Overtime ${formatDuration(summary.overtimeMinutes)}`,
    padding + context.measureText(summaryText).width,
    118
  );

  if (closure.note) {
    context.fillStyle = closure.note.startsWith("Holiday") ? colors.holiday : colors.weekend;
    context.font = "700 14px Segoe UI, Arial, sans-serif";
    context.fillText(closure.note, padding, 146);
  }

  let x = padding;
  context.fillStyle = colors.header;
  context.fillRect(padding, tableTop, tableWidth, headHeight);
  context.fillStyle = colors.headerLine;
  context.fillRect(padding, tableTop + headHeight - 2, tableWidth, 2);

  context.fillStyle = colors.headerText;
  context.font = "700 13px Segoe UI, Arial, sans-serif";
  context.textBaseline = "middle";
  columns.forEach((column) => {
    context.textAlign = column.align === "center" ? "center" : "left";
    const textX = column.align === "center" ? x + (column.width / 2) : x + 12;
    context.fillText(column.label, textX, tableTop + (headHeight / 2));
    x += column.width;
  });

  rows.forEach((row, rowIndex) => {
    const y = tableTop + headHeight + (rowIndex * rowHeight);
    context.fillStyle = rowIndex % 2 === 0 ? colors.row : colors.rowAlt;
    context.fillRect(padding, y, tableWidth, rowHeight);
    context.strokeStyle = colors.line;
    context.beginPath();
    context.moveTo(padding, y + rowHeight);
    context.lineTo(padding + tableWidth, y + rowHeight);
    context.stroke();

    let cellX = padding;
    columns.forEach((column) => {
      if (column.image) {
        const photo = images.get(row[column.key]);
        const size = 46;
        const photoX = cellX + ((column.width - size) / 2);
        const photoY = y + ((rowHeight - size) / 2);
        if (photo) {
          drawCoverImage(context, photo, photoX, photoY, size, colors.photoBorder);
        } else {
          context.fillStyle = colors.muted;
          context.font = "600 16px Segoe UI, Arial, sans-serif";
          context.textAlign = "center";
          context.fillText("—", cellX + (column.width / 2), y + (rowHeight / 2));
        }
      } else {
        const mark = column.key === "checkIn" && row.isLate
          ? "Late"
          : column.key === "checkOut" && row.isEarlyCheckout
            ? "Early checkout"
            : "";
        const isStatus = column.key === "status";
        const isOvertime = column.key === "overtime" && row.overtimeMinutes > 0;
        context.textAlign = column.align === "center" ? "center" : "left";
        const textX = column.align === "center" ? cellX + (column.width / 2) : cellX + 12;
        const textY = y + (rowHeight / 2);

        if (mark) {
          const timeText = column.key === "checkIn" ? row.checkIn : row.checkOut;
          context.fillStyle = colors.text;
          context.font = "600 14px Segoe UI, Arial, sans-serif";
          context.fillText(timeText, textX, textY);
          const timeWidth = context.measureText(`${timeText} `).width;
          context.fillStyle = colors.mark;
          context.font = "800 14px Segoe UI, Arial, sans-serif";
          context.fillText(
            fitText(context, mark, Math.max(0, column.width - 20 - timeWidth)),
            textX + timeWidth,
            textY
          );
        } else {
          context.fillStyle = isOvertime ? colors.overtime : isStatus ? row.status.color : colors.text;
          context.font = isStatus || isOvertime
            ? "800 14px Segoe UI, Arial, sans-serif"
            : "600 14px Segoe UI, Arial, sans-serif";
          const value = isStatus ? row.status.label : row[column.key];
          context.fillText(fitText(context, value, column.width - 20), textX, textY);
        }
      }

      cellX += column.width;
    });
  });

  context.fillStyle = colors.footer;
  context.font = "600 12px Segoe UI, Arial, sans-serif";
  context.textAlign = "left";
  context.textBaseline = "top";
  context.fillText("Employees are listed in alphabetical order.", padding, height - 28);
}

async function renderDailySheet({ dateLabel, closure, rows, summary, includeImages }) {
  const images = includeImages
    ? new Map(await Promise.all(
      [...new Set(rows.flatMap((row) => [row.checkInImage, row.checkOutImage]).filter(Boolean))]
        .map(async (src) => [src, await loadImage(src)])
    ))
    : new Map();

  const columns = getSheetColumns(includeImages);
  const tableWidth = getSheetTableWidth(columns);
  const width = tableWidth + 72;
  const { height } = getSheetLayout(closure, rows.length);
  const canvas = document.createElement("canvas");
  const scale = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.ceil(width * scale);
  canvas.height = Math.ceil(height * scale);
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to create the attendance image.");
  }

  context.scale(scale, scale);
  drawDailySheet(context, {
    dateLabel,
    closure,
    rows,
    summary,
    images,
    columns,
    tableWidth,
    width
  });

  return canvas;
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Unable to create the attendance image."));
          return;
        }
        resolve(blob);
      }, "image/png");
    } catch (error) {
      reject(error);
    }
  });
}

function savePngBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function downloadDailyAttendancePng({ dateKey, dateLabel, closure, rows, summary, includeImages }) {
  let canvas;
  try {
    canvas = await renderDailySheet({
      dateLabel,
      closure,
      rows,
      summary,
      includeImages
    });
    const blob = await canvasToPngBlob(canvas);
    savePngBlob(blob, `daily-attendance-${dateKey}.png`);
    return;
  } catch {
    canvas = await renderDailySheet({
      dateLabel,
      closure,
      rows,
      summary,
      includeImages: false
    });
    const blob = await canvasToPngBlob(canvas);
    savePngBlob(blob, `daily-attendance-${dateKey}.png`);
  }
}

function blockCapturedImageSave(event) {
  event.preventDefault();
}

function DailyAttendanceTab({ users = [], usersLoading = false, usersError = "" }) {
  const [selectedDate, setSelectedDate] = useState(() => getLocalDateKey());
  const [records, setRecords] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [holidays, setHolidays] = useState(() => fetchHolidayRecords());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const employees = directory.length > 0 ? directory : users;
  const waitingForEmployees = directoryLoading && employees.length === 0;

  const selectedDateValue = /^\d{4}-\d{2}-\d{2}$/.test(selectedDate) ? selectedDate : getLocalDateKey();
  const selectedDateObject = parseDateKey(selectedDateValue);
  const viewMonth = selectedDateObject.getMonth();
  const viewYear = selectedDateObject.getFullYear();

  useEffect(() => {
    setHolidays(fetchHolidayRecords());
  }, [selectedDateValue]);

  useEffect(() => {
    let cancelled = false;
    setDirectoryLoading(true);

    fetchTecgymAdminUsers()
      .then((list) => {
        if (!cancelled) {
          setDirectory(Array.isArray(list) ? list : []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          showToast(error.message || "Failed to load employees.", "error");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDirectoryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchAttendanceRecords(viewMonth + 1, viewYear)
      .then((nextRecords) => {
        if (!cancelled) {
          setRecords(nextRecords);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          showToast(error.message || "Failed to load attendance.", "error");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [viewMonth, viewYear]);

  const closure = useMemo(
    () => getDayClosure(selectedDateValue, holidays),
    [holidays, selectedDateValue]
  );
  const rows = useMemo(
    () => buildDailyRows(employees, records, selectedDateValue, closure),
    [closure, employees, records, selectedDateValue]
  );
  const summary = useMemo(() => summarizeRows(rows, closure), [closure, rows]);
  const dateLabel = formatDailyDateLabel(selectedDateValue);

  const handleDownload = async () => {
    if (downloading || loading || waitingForEmployees || usersLoading) {
      return;
    }

    try {
      setDownloading(true);
      await downloadDailyAttendancePng({
        dateKey: selectedDateValue,
        dateLabel,
        closure,
        rows,
        summary,
        includeImages: true
      });
      showToast("Daily attendance image downloaded.", "success");
    } catch (error) {
      showToast(error.message || "Unable to download the attendance image.", "error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section style={sectionStyle}>
      <div style={toolbarStyle}>
        <label style={dateFieldStyle}>
          <span style={dateFieldLabelStyle}>Date</span>
          <input
            type="date"
            value={selectedDateValue}
            onChange={(event) => setSelectedDate(event.target.value || getLocalDateKey())}
            style={dateInputStyle}
          />
        </label>
        <div style={toolbarMetaStyle}>
          <strong style={dateHeadingStyle}>{dateLabel}</strong>
          <span style={toolbarHintStyle}>Employees are listed in alphabetical order.</span>
        </div>
        <button
          type="button"
          style={downloading || loading || waitingForEmployees ? disabledButtonStyle : downloadButtonStyle}
          onClick={handleDownload}
          disabled={downloading || loading || waitingForEmployees}
        >
          {downloading ? "Downloading..." : "Download"}
        </button>
      </div>

      <div style={summaryGridStyle}>
        <SummaryCard label="Total" value={summary.total} />
        <SummaryCard label="Present" value={summary.present} />
        <SummaryCard label="Checked in" value={summary.checkedIn} />
        <SummaryCard label="Late" value={summary.late} />
        <SummaryCard label="Absent" value={summary.absent} />
        <SummaryCard
          label="Overtime"
          value={formatDuration(summary.overtimeMinutes)}
          valueStyle={overtimeSummaryValueStyle}
        />
      </div>

      {closure.note && (
        <div style={closure.note.startsWith("Holiday") ? holidayBannerStyle : weekendBannerStyle}>
          {closure.note}
        </div>
      )}

      {usersError && <div style={errorStyle}>{usersError}</div>}

      {waitingForEmployees || usersLoading || loading ? (
        <div style={emptyStateStyle}>Loading daily attendance...</div>
      ) : rows.length === 0 ? (
        <div style={emptyStateStyle}>No employees found for daily attendance.</div>
      ) : (
        <div style={tableShellStyle}>
          <table style={tableStyle}>
            <thead>
              <tr style={tableHeadRowStyle}>
                {getSheetColumns(true).map((column) => (
                  <th key={column.key} style={tableHeaderStyle}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={`${row.name}-${row.index}`}
                  style={{
                    borderBottom: "1px solid var(--home-border, #444)",
                    backgroundColor: index % 2 === 0 ? "transparent" : "var(--home-card-hover, #f1f3f5)"
                  }}
                >
                  <td style={tableCellStyle}>{row.index}</td>
                  <td style={nameCellStyle}>{row.name}</td>
                  <td style={{ ...tableCellStyle, whiteSpace: "nowrap" }}>
                    {row.checkIn}
                    {row.isLate ? <span style={attendanceMarkStyle}> Late</span> : ""}
                  </td>
                  <td style={tableCellStyle}>
                    <AttendancePhoto src={row.checkInImage} alt={`${row.name} check in`} onOpen={setPreviewImage} />
                  </td>
                  <td style={{ ...tableCellStyle, whiteSpace: "nowrap" }}>
                    {row.checkOut}
                    {row.isEarlyCheckout ? <span style={attendanceMarkStyle}> Early checkout</span> : ""}
                  </td>
                  <td style={tableCellStyle}>
                    <AttendancePhoto src={row.checkOutImage} alt={`${row.name} check out`} onOpen={setPreviewImage} />
                  </td>
                  <td style={tableCellStyle}>{row.working}</td>
                  <td style={tableCellStyle}>{row.deficit}</td>
                  <td style={row.overtimeMinutes > 0 ? overtimeCellStyle : tableCellStyle}>{row.overtime}</td>
                  <td style={{ ...tableCellStyle, color: row.status.color, fontWeight: 800 }}>{row.status.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewImage && (
        <div
          style={modalOverlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label="Attendance photo"
          onClick={() => setPreviewImage("")}
          onContextMenu={blockCapturedImageSave}
        >
          <ProtectedCapturedImage src={previewImage} alt="Attendance" style={previewImageStyle} />
        </div>
      )}
    </section>
  );
}

function SummaryCard({ label, value, valueStyle }) {
  return (
    <div style={summaryCardStyle}>
      <span style={summaryLabelStyle}>{label}</span>
      <strong style={valueStyle ? { ...summaryValueStyle, ...valueStyle } : summaryValueStyle}>{value}</strong>
    </div>
  );
}

function AttendancePhoto({ src, alt, onOpen }) {
  if (!src) {
    return <span>—</span>;
  }

  return (
    <button
      type="button"
      style={thumbButtonStyle}
      onClick={() => onOpen(src)}
      onContextMenu={blockCapturedImageSave}
      title={`View ${alt}`}
    >
      <ProtectedCapturedImage src={src} alt={alt} style={thumbImageStyle} />
    </button>
  );
}

function ProtectedCapturedImage({ src, alt, style }) {
  return (
    <span style={capturedImageFrameStyle} onContextMenu={blockCapturedImageSave}>
      <img
        src={src}
        alt={alt}
        draggable={false}
        onContextMenu={blockCapturedImageSave}
        onDragStart={blockCapturedImageSave}
        style={{ ...style, ...capturedImageStyle }}
      />
      <span aria-hidden="true" style={capturedImageShieldStyle} onContextMenu={blockCapturedImageSave} />
    </span>
  );
}

const sectionStyle = {
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.25)"
};

const toolbarStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
  marginBottom: "18px"
};

const dateFieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  minWidth: "180px"
};

const dateFieldLabelStyle = {
  color: "var(--home-heading, #fff)",
  fontSize: "13px",
  fontWeight: 800
};

const dateInputStyle = {
  border: "1px solid var(--home-border, #ced4da)",
  borderRadius: "10px",
  backgroundColor: "var(--home-card-bg, #fff)",
  color: "var(--home-heading, #212529)",
  padding: "12px 14px",
  fontSize: "14px",
  fontWeight: 700,
  outline: "none"
};

const toolbarMetaStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: 1,
  minWidth: "220px"
};

const dateHeadingStyle = {
  color: "var(--home-heading, #212529)",
  fontSize: "18px"
};

const toolbarHintStyle = {
  color: "var(--home-muted, #888)",
  fontSize: "13px",
  fontWeight: 600
};

const downloadButtonStyle = {
  border: "1px solid #187718",
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "12px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
  marginLeft: "auto",
  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
};

const disabledButtonStyle = {
  ...downloadButtonStyle,
  opacity: 0.55,
  cursor: "not-allowed",
  boxShadow: "none"
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
  gap: "10px",
  marginBottom: "16px"
};

const summaryCardStyle = {
  border: "1px solid var(--home-border, #ced4da)",
  borderRadius: "10px",
  backgroundColor: "var(--home-card-hover, #f1f3f5)",
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  gap: "6px"
};

const summaryLabelStyle = {
  color: "var(--home-muted, #495057)",
  fontSize: "12px",
  fontWeight: 700
};

const summaryValueStyle = {
  color: "var(--home-heading, #212529)",
  fontSize: "18px",
  fontWeight: 800
};

const overtimeSummaryValueStyle = {
  color: "#15803d"
};

const holidayBannerStyle = {
  marginBottom: "16px",
  border: "1px solid rgba(147, 197, 253, 0.45)",
  borderRadius: "10px",
  backgroundColor: "rgba(59, 130, 246, 0.18)",
  color: "#93c5fd",
  padding: "12px 14px",
  fontWeight: 800
};

const weekendBannerStyle = {
  marginBottom: "16px",
  border: "1px solid rgba(252, 165, 165, 0.45)",
  borderRadius: "10px",
  backgroundColor: "rgba(127, 29, 29, 0.22)",
  color: "#fca5a5",
  padding: "12px 14px",
  fontWeight: 800
};

const errorStyle = {
  marginBottom: "16px",
  color: "#fca5a5",
  fontWeight: 700
};

const emptyStateStyle = {
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px dashed var(--home-border, #444)",
  borderRadius: "14px",
  color: "var(--home-muted, #aaa)",
  padding: "28px",
  textAlign: "center",
  fontWeight: 600
};

const tableShellStyle = {
  overflowX: "auto",
  border: "1px solid var(--home-border, #ced4da)",
  borderRadius: "10px",
  backgroundColor: "var(--home-card-bg, #fff)",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)"
};

const tableStyle = {
  width: "100%",
  minWidth: "1100px",
  borderCollapse: "collapse",
  fontSize: "14px"
};

const tableHeadRowStyle = {
  backgroundColor: "var(--home-card-hover, #f1f3f5)",
  borderBottom: "2px solid #187718"
};

const tableHeaderStyle = {
  padding: "14px 12px",
  color: "var(--home-heading, #212529)",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.3px",
  textAlign: "left",
  whiteSpace: "nowrap"
};

const attendanceMarkStyle = {
  color: "#dc2626",
  fontWeight: 800
};

const tableCellStyle = {
  padding: "12px",
  color: "var(--home-heading, #212529)",
  verticalAlign: "middle"
};

const overtimeCellStyle = {
  ...tableCellStyle,
  color: "#15803d",
  fontWeight: 800
};

const nameCellStyle = {
  ...tableCellStyle,
  fontWeight: 800
};

const thumbButtonStyle = {
  border: "none",
  background: "transparent",
  padding: 0,
  cursor: "pointer"
};

const thumbImageStyle = {
  width: "52px",
  height: "52px",
  objectFit: "cover",
  borderRadius: "8px",
  border: "1px solid var(--home-border, #ced4da)",
  display: "block"
};

const capturedImageFrameStyle = {
  position: "relative",
  display: "inline-flex"
};

const capturedImageStyle = {
  pointerEvents: "none",
  userSelect: "none",
  WebkitUserDrag: "none"
};

const capturedImageShieldStyle = {
  position: "absolute",
  inset: 0
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  backgroundColor: "rgba(0, 0, 0, 0.65)",
  backdropFilter: "blur(4px)"
};

const previewImageStyle = {
  maxWidth: "90vw",
  maxHeight: "90vh",
  borderRadius: "12px",
  border: "1px solid #444"
};

export default DailyAttendanceTab;
